import { Box, Col, getCSSVariable, Image, Row, Text } from '@opendoor/bricks-next';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';
import type { AddressSearchLocation } from 'components/globals';
import NewSellerFlowAddressInput from 'components/shared/NewSellerFlowAddressInput';
import type { ICompareHero } from 'declarations/contentful';
import { optimizeContentfulImageUrl } from 'helpers/imageUrl';

const RenderCompareHero = (entry: ICompareHero) => {
  const { fields } = entry;
  const heroImageUrl = fields.heroImage?.fields?.file?.url;
  const optimizedHeroImageUrl = heroImageUrl
    ? optimizeContentfulImageUrl(heroImageUrl, { width: 960, quality: 80 })
    : undefined;

  return (
    <Box
      py="$40x"
      $smallerThanMD={{ py: '$24x' }}
      $smallerThanLG={{ py: '$20x' }}
      backgroundColor="$backgroundPrimary"
    >
      <Box display="block" maxWidth={1120} px="$12x" width="100%" mx="auto">
        <Box
          flexDirection="column"
          gap="$16x"
          alignItems="center"
          $largerThanSM={{ flexDirection: 'row' }}
        >
          <Col flex={1.1} gap="$10x">
            <Text tag="span" typography="$labelSmall" color="$contentAccentBrand">
              {fields.eyebrow}
            </Text>
            <Col gap="$10x">
              <Text
                tag="h1"
                typography="$headerLarge"
                color="$contentPrimary"
                $smallerThanMD={{ typography: '$headerSmall' }}
                $smallerThanLG={{ typography: '$headerMedium' }}
              >
                {fields.title}{' '}
                {fields.titleAccent && (
                  <Text
                    tag="span"
                    color="$contentAccentBrand"
                    typography="$headerLarge"
                    $smallerThanMD={{ typography: '$headerSmall' }}
                    $smallerThanLG={{ typography: '$headerMedium' }}
                  >
                    {fields.titleAccent}
                  </Text>
                )}
              </Text>
              <Text
                tag="p"
                typography="$bodyLarge"
                color="$contentSecondary"
                maxWidth="$616x"
                $platform-web={{ lineHeight: '1.6' }}
              >
                {fields.description}
              </Text>
            </Col>
            <Col gap="$10x">
              <Box maxWidth="$516x" width="100%">
                <NewSellerFlowAddressInput
                  hideLabel
                  inputLocation={
                    (fields.addressInputLocation || 'hero-cta') as AddressSearchLocation
                  }
                  analyticsPrefix={fields.addressInputAnalyticsPrefix || 'compare-hero'}
                  trackingTaxonomy="new_homepage"
                  placeholder={fields.addressInputPlaceholder || 'Enter your home address'}
                  channel="DEFAULT"
                  size="medium"
                />
              </Box>
              {fields.trustBadges && fields.trustBadges.length > 0 && (
                <Row gap="$12x" flexWrap="wrap">
                  {fields.trustBadges.map((item) => (
                    <Row key={item} gap="$4x" alignItems="center">
                      <IconCircleCheckFilled
                        size={18}
                        color={getCSSVariable('$contentStateSuccessDefault', 'color')}
                      />
                      <Text tag="span" typography="$bodySmall" color="$contentSecondary">
                        {item}
                      </Text>
                    </Row>
                  ))}
                </Row>
              )}
            </Col>
          </Col>

          {fields.heroImage && (
            <Box
              flex={0.9}
              alignItems="center"
              justifyContent="center"
              $smallerThanLG={{ display: 'none' }}
            >
              <Image
                source={{ uri: optimizedHeroImageUrl }}
                alt={fields.heroImageAlt || ''}
                width="100%"
                maxWidth={480}
                height={480}
                borderRadius="$8x"
                $platform-web={{ aspectRatio: '1', objectFit: 'cover' }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const CompareHero: EntryComponent<ICompareHero> = {
  render: RenderCompareHero,
};

export default CompareHero;
